import type { CompanyActivityLevel } from '@/types/Company'

/**
 * Referentiel officiel GERS (points de vente, type 1 = pharmacies) : journal des imports et
 * rapprochement CIP par CIP avec la base (GET /gers_reconciliations, materialise cote API).
 */

/**
 * `only_eurodep` (12/10/2026) : CIP inconnu du GERS et de la base, officine cliente du flux
 * Eurodep du lundi. `only_pharmupp` (15/10/2026) : CIP inconnu du GERS, d'Eurodep et de la base,
 * officine de plus d'un titulaire connu proposee par la synchro PharmUPP. Avec `only_gers`, ce
 * sont les lignes « a creer » (GERS_CREATION_STATUSES).
 */
export type GersReconciliationStatus = 'matched' | 'only_db' | 'only_gers' | 'only_eurodep' | 'only_pharmupp'

/** Statuts des lignes « a creer » : pilule « Officines a creer », action `create`. */
export const GERS_CREATION_STATUSES: GersReconciliationStatus[] = ['only_gers', 'only_eurodep', 'only_pharmupp']

/** Verdict de la base Sirene locale sur le SIRET Eurodep. */
export type GersEurodepInseeStatus = 'active' | 'inactive' | 'unchecked'

/** Zone deduite du premier chiffre du CIP7 : 2 = metropole, 3 = DOM-TOM. */
export type GersCipZone = 'metropole' | 'domtom' | 'other'

/** Valeurs de `companyStatusSociety` ; `unchecked` est une sentinelle NON nulle pour rester filtrable. */
export type GersInseeStatus = 'active' | 'inactive' | 'unchecked'

/** Types de proposition (cles i18n `components.gers.proposal.*`). */
export type GersProposalKind =
  | 'geo'
  | 'name'
  | 'owner'
  | 'merge'
  | 'replacement'
  | 'archive'
  // CIP repris par la fiche d'une autre equipe : transfert, rattachement propose (18/10/2026).
  | 'regroup'
  // Cession probable (titulaire GERS different, mis a jour apres le dernier changement connu).
  | 'cession'

/**
 * Lien OFFICIEL du GERS pour une fiche vivante dont le CIP est supprime du referentiel (fichier
 * complet, colonnes « Suppression » / « Remplace par ») : fusion vers la fiche qui porte le CIP
 * final, changement de CIP si personne ne le porte, archivage sans successeur actif.
 */
export type GersLinkKind = 'merge' | 'change_cip' | 'archive'

/**
 * Raison d'un lien officiel quand elle change le geste : regroupement (archivage), ou fiche du
 * CIP final archivée (fusion en sens inverse, 22/10/2026).
 */
export type GersLinkReason = 'owner_differs' | 'target_set_aside'

/** Fin de la chaine « Remplace par » (cles i18n `components.gers.finalKind.*`). */
export type GersFinalKind = 'active' | 'deleted' | 'other_type' | 'none'

/** Verdict du GERS sur une fusion DEJA faite ; « a verifier » = suspect + weak. */
export type GersMergedVerdict = 'confirmed' | 'plausible' | 'weak' | 'suspect'

export const GERS_MERGED_VERDICTS_TO_VERIFY: GersMergedVerdict[] = ['suspect', 'weak']

/** Raison du verdict (cles i18n `components.gers.mergedVerdictReason.*`). */
export type GersMergedVerdictReason =
  | 'same_cip'
  | 'gers_chain'
  | 'reverse_direction'
  | 'both_active'
  | 'source_active'
  | 'other_successor'
  | 'different_zip'
  | 'same_zip_same_siren'
  | 'same_zip_same_address'
  | 'same_zip_different_siren'
  | 'same_zip_only'
  | 'zip_unknown'

export type GersMergeCandidateBy =
  | 'gers_link'
  | 'finess'
  | 'siret'
  | 'siren'
  | 'name_zip'
  | 'owner_zip'
  | 'address_zip'

/**
 * Force d'un candidat : strong (score >= 70) | medium (>= 45) | low (>= 25, hors actions en
 * masse) ; `official` = lien officiel « Remplace par » du GERS, eligible aux actions en masse.
 */
export type GersMatchStrength = 'strong' | 'medium' | 'low' | 'official'

/** Criteres du score (cles i18n `components.gers.reason.*`). */
export type GersMatchReason =
  | 'gers_link'
  | 'same_finess'
  | 'same_siret'
  | 'same_siren'
  | 'different_siren'
  | 'same_name'
  | 'same_generic_name'
  | 'same_owner'
  | 'different_owner'
  | 'same_address'
  | 'same_street_number'
  | 'same_groupement'
  | 'different_groupement'

export type GersMergePreview = {
  score: number | null
  strength: GersMatchStrength | null
  reasons: GersMatchReason[]
  source: { id: number; cip: string | null; activityScore: number }
  target: { id: number; cip: string | null; activityScore: number }
  sourceHasMoreActivity: boolean
  bothActive: boolean
}

export type GersReconciliationRow = {
  id: number
  cip: string
  status: GersReconciliationStatus
  cipZone: GersCipZone
  // cote base
  companyId: number | null
  companyName: string | null
  companyAddress: string | null
  companyZipCode: string | null
  companyCity: string | null
  companyCodeUga: string | null
  companySiret: string | null
  /** Statut INSEE : active | inactive | unchecked (a verifier : jamais controlee, sans SIRET ou sans resultat). */
  companyStatusSociety: GersInseeStatus | null
  companyControlLastDate: string | null
  /** Dernière facture signée de la fiche (Insight, Sound ou PLV) : fonde le niveau d'activité. */
  companyLastSignedInvoiceAt?: string | null
  /** Fin du programme en cours de la fiche (AAAA-MM-JJ, 25/10/2026) : l'autre date du niveau d'activité. */
  companyRunningProgramUntil?: string | null
  /** Niveau d'activité dérivé par l'API (règle des trois mois) ; null sans fiche. */
  companyActivityLevel?: CompanyActivityLevel | null
  /** E-mail du compte titulaire ; `<CIP>@mail.fr` = adresse générique, sans boîte. */
  companyOwnerEmail?: string | null
  /** E-mail de la pharmacie (contact de la fiche), quand il est renseigné. */
  companyContactEmail?: string | null
  /** Dernière connexion du titulaire, tous canaux, à la date du dernier calcul de la ligne. */
  companyOwnerLastActivityAt?: string | null
  companyValidationStatus: string | null
  companyArchived: boolean
  /** Fiche mise de cote : supprimee OU exclue du panel (statut Archive). */
  companySetAside: boolean
  companyCipTest: boolean
  companyOwnerName: string | null
  companyGroupementName: string | null
  // titulaire « sans nom » (analyse nocturne) : nom nul / vide / libelle de remplissage
  ownerPlaceholder: boolean
  /** Nom propose (GERS puis CNOP), null si aucune source ne le fournit. */
  ownerProposalName: string | null
  ownerProposalSource: GersOwnerProposalSource | null
  /** 100 pour le GERS, score du rapprochement d'etablissement CNOP (0-100). */
  ownerProposalScore: number | null
  /** Nom du titulaire porte par le compte Store (CS-Cart), null sans compte ou non verifie. */
  ownerStoreName: string | null
  /** Compte Store encore au libelle de remplissage (« Le pharmacien titulaire »). */
  ownerStorePlaceholder: boolean
  // cote GERS
  gersName: string | null
  gersAddress: string | null
  gersZipCode: string | null
  gersCity: string | null
  gersCodeUga: string | null
  gersOwnerName: string | null
  gersOwnerUpdatedAt: string | null
  gersCreatedAt: string | null
  /**
   * Type du point de vente au GERS (25/10/2026) : '1' pharmacie, '3' mutualiste, '4' CARMI (régime
   * minier) ; null hors référentiel. Une mutualiste ou une CARMI n'est jamais « à créer ».
   */
  gersPosType: '1' | '3' | '4' | null
  // ecarts (lignes matched, fiche active)
  /** Au moins un ecart corrigeable par les actions geo / name. */
  hasDiff: boolean
  /** Au moins une proposition, tous statuts (ecart geo / nom, titulaire, fusion, remplacement). */
  hasProposal: boolean
  /** Types de proposition separes par des virgules (cf. GersProposalKind), null sans proposition. */
  proposalKinds: string | null
  diffName: boolean
  diffAddress: boolean
  diffZipCode: boolean
  diffCity: boolean
  diffCodeUga: boolean
  diffOwner: boolean
  ownerMissing: boolean
  // fusion (lignes only_db)
  hasMergeCandidate: boolean
  mergeCandidateCompanyId: number | null
  mergeCandidateCip: string | null
  mergeCandidateName: string | null
  mergeCandidateBy: GersMergeCandidateBy | null
  mergeScore: number | null
  mergeStrength: GersMatchStrength | null
  mergeReasons: GersMatchReason[] | null
  // fusion REFUSEE a la main : « ces deux fiches ne sont pas la meme » (paire symetrique)
  /** Date du dernier refus portant sur cette fiche, null si aucun. */
  mergeRejectedAt: string | null
  /** CIP de l'autre fiche de la paire refusee. */
  mergeRejectedCip: string | null
  mergeRejectedBy: string | null
  // fusion DEJA faite (fiche supprimee par merge_source)
  hasMergedInto: boolean
  mergedIntoCompanyId: number | null
  mergedIntoCip: string | null
  mergedIntoName: string | null
  /** Date de la fusion deja faite. */
  mergedIntoAt: string | null
  /** Verdict du GERS sur la fusion deja faite (lecture seule). */
  mergedVerdict: GersMergedVerdict | null
  mergedVerdictReason: GersMergedVerdictReason | null
  // CIP SUPPRIME du referentiel (fichier GERS complet)
  /** Date de suppression du CIP de la fiche au GERS. */
  gersDeletedAt: string | null
  /** Successeur direct (« Remplace par »). */
  gersReplacedByCip: string | null
  /** Dernier CIP de la chaine de remplacement. */
  gersFinalCip: string | null
  gersFinalKind: GersFinalKind | null
  /** Lien officiel pour une fiche vivante au CIP supprime, null sinon. */
  gersLinkKind: GersLinkKind | null
  /** Le GERS confirme une fusion refusee a la main : signale, jamais leve automatiquement. */
  gersLinkRejected: boolean
  /**
   * `owner_differs` : la chaine aboutit a une fiche vivante sans personne en commun parmi les
   * titulaires, co-titulaires et anciens titulaires (regroupement, cession) — le lien est alors
   * un ARCHIVAGE, pas une fusion (API GersOfficialLinkResolver::sameHolders).
   * `target_set_aside` (lien `merge`) : la seule fiche du CIP final est archivée, la fiche
   * active ne doit pas l'être — la fiche archivée la rejoint, à la main (pas de candidat de
   * fusion). Null sinon.
   */
  gersLinkReason: GersLinkReason | null
  /**
   * Fiche au bout du lien, quel que soit le geste (regroupement, fusion officielle, fiche
   * archivée). La fiche du CIP final la relit (filtre `gersLinkTargetCompanyId`) pour dire
   * quelles fiches y ont été regroupées. Jamais un candidat de fusion pour `owner_differs`.
   */
  gersLinkTargetCompanyId: number | null
  /** Son nom, figé au calcul du rapprochement (null avant le premier recalcul). */
  gersLinkTargetCompanyName?: string | null
  // CESSION PROBABLE (lot 7 « Qualite des fiches », API GersCessionDetector)
  /**
   * Ligne commune, fiche vivante non archivee, titulaire GERS different de celui de la fiche et
   * mis a jour au GERS (`gersOwnerUpdatedAt`) APRES `companyOwnerSince`.
   */
  cessionProbable: boolean
  /** Depuis quand le titulaire actuel est connu : dernier changement, sinon creation du compte, sinon de la fiche. */
  companyOwnerSince: string | null
  // remplacement (lignes only_gers) : fiche active EN TROP que ce nouveau CIP remplace probablement
  hasReplacement: boolean
  replacementCompanyId: number | null
  replacementCip: string | null
  replacementName: string | null
  replacementBy: GersReplacementBy | null
  replacementScore: number | null
  replacementStrength: GersMatchStrength | null
  replacementReasons: GersMatchReason[] | null
  // flux Eurodep du lundi (lignes a creer) : ce que le GERS n'a pas — e-mail, SIRET, telephone
  eurodepKnown: boolean
  eurodepName: string | null
  eurodepAddress: string | null
  eurodepZipCode: string | null
  eurodepCity: string | null
  /** Tel qu'Eurodep l'ecrit (civilite, ordre variable) : affiche, jamais ecrit sur la fiche. */
  eurodepOwnerName: string | null
  eurodepEmail: string | null
  /** E-mail deja porte par un compte : la creation prend l'e-mail generique du CIP. */
  eurodepEmailTaken: boolean
  /** Fiche du compte qui porte l'e-mail ; = replacementCompanyId quand c'est la fiche que ce CIP remplace. */
  eurodepEmailCompanyId?: number | null
  eurodepEmailCompanyCip?: string | null
  eurodepSiret: string | null
  eurodepPhone: string | null
  eurodepClientType: string | null
  eurodepInseeStatus: GersEurodepInseeStatus | null
  eurodepInseeNaf: string | null
  eurodepFirstSeenAt: string | null
  eurodepUpdatedAt: string | null
  // PharmUPP (lignes a creer) : officine de plus d'un titulaire connu — la fiche sera creee pour
  // CE compte, dans CE groupement (API GersPharmUppRows).
  pharmuppKnown?: boolean
  pharmuppName?: string | null
  pharmuppAddress?: string | null
  pharmuppZipCode?: string | null
  pharmuppCity?: string | null
  /** E-mail de l'officine selon UPP : e-mail de contact de la fiche creee. */
  pharmuppEmail?: string | null
  /** Compte du titulaire ; null si le compte n'existe plus (creation ordinaire). */
  pharmuppHolderUserId?: number | null
  /** « Prenom Nom — e-mail ». */
  pharmuppHolderLabel?: string | null
  pharmuppGroupementId?: number | null
  pharmuppGroupementName?: string | null
  pharmuppFirstSeenAt?: string | null
  computedAt: string
}

export type GersReplacementBy = 'gers_link' | 'name_zip' | 'owner_zip' | 'address_zip'

/**
 * Compteurs des pilules (GET gers_reconciliation_counts), filtrables par zone CIP. `gers_*` :
 * fiches a traiter (ni supprimees ni exclues du panel) au CIP supprime du GERS, par lien
 * officiel ; `gers_link_rejected` : refus de fusion contredits par le GERS (toutes fiches
 * vivantes) ; `merges_*` : verdict des fusions deja faites.
 */
export type GersReconciliationCounts = Partial<
  Record<
    | GersReconciliationStatus
    // Lignes « a creer » : only_gers + only_eurodep + only_pharmupp.
    | 'to_create'
    | 'only_db_to_process'
    | 'only_db_excluded'
    | 'only_db_merged'
    | 'owner_placeholder'
    | 'owner_store_placeholder'
    | 'cession_probable'
    | 'gers_deleted'
    | 'gers_deleted_merge'
    | 'gers_deleted_change_cip'
    | 'gers_deleted_archive'
    | 'gers_archive_insee_closed'
    | 'gers_archive_owner_differs'
    | 'gers_archive_no_successor'
    | 'gers_link_rejected'
    | 'merges_confirmed'
    | 'merges_plausible'
    | 'merges_weak'
    | 'merges_suspect'
    | 'merges_to_verify',
    number
  >
>

export type GersReferenceImport = {
  id: number
  fileName: string | null
  importedAt: string
  importedByName: string | null
  rowsTotal: number
  rowsPharmacies: number
  matched: number
  onlyDb: number
  onlyGers: number
  stats: Record<string, number> | null
}

/**
 * Actions de POST gers_reconciliations/apply. Liste UNIQUE : le type en est derive, et le
 * schema Zod de la mutation la consomme telle quelle (src/ApiMutationEndpoint.ts). Une action
 * ajoutee ici est donc acceptee partout ; l'ajouter a un seul des deux endroits — ce qui a
 * laisse « Nom titulaire », « Nom titulaire Store » et le refus de fusion inoperants, rejetes
 * par Zod avant l'appel — n'est plus possible.
 */
export const GERS_APPLY_ACTIONS = [
  'geo',
  'name',
  'create',
  'merge',
  'changeCip',
  'ownerName',
  'rejectMerge',
  'restoreMerge',
  // CIP supprime du GERS sans successeur actif : statut Archive (motif date), compte Store
  // desactive. Propose, jamais applique sans selection humaine.
  'archive',
  // Cession probable : une tache CRM « Cession probable (GERS) » par fiche, service Qualite des
  // donnees. Rien ne change sur la fiche : le changement de titulaire se fait depuis la tache.
  'cessionTask',
  // Regroupement au GERS (lien `archive`, motif `owner_differs`, CIP final porte par une fiche
  // active) : la fiche est rattachee en `regroupement` a celle du CIP final, close a la date de
  // suppression du CIP. Meme SIREN des deux cotes : ignoree (c'est une fusion).
  'regroup',
] as const

export type GersApplyAction = (typeof GERS_APPLY_ACTIONS)[number]

/** Source du nom de titulaire propose pour un titulaire « sans nom ». */
export type GersOwnerProposalSource = 'gers' | 'cnop'

export type GersApplyResult = {
  action: GersApplyAction
  requested: number
  updated: number
  skipped: number
  errors: string[]
  /** Creation : titulaires ecrits depuis le CNOP (fiable) et depuis le GERS. */
  ownerFromCnop?: number
  ownerFromGers?: number
  /** Creation d'une ligne Eurodep sans titulaire CNOP fiable : fiche « sans nom ». */
  ownerNone?: number
  /** Creation d'une officine de plus proposee par PharmUPP : fiche donnee au titulaire connu. */
  ownerFromPharmUpp?: number
  /** Creation d'UNE ligne : nom saisi dans « Créer la fiche », différent de la proposition. */
  ownerTyped?: number
  /** Creation : fiches qui ont pris l'e-mail / le SIRET d'Eurodep. */
  emailFromEurodep?: number
  siretFromEurodep?: number
  /** Creation d'UNE ligne : e-mail / SIRET saisis qui ne sont pas ceux d'Eurodep. */
  emailTyped?: number
  siretTyped?: number
  /** Creation : invitations REELLEMENT parties (demandees, vrai e-mail, compte cree). */
  invitationSent?: number
  /** Taches de cession : fiches ignorees car une demande de cession y est deja ouverte. */
  alreadyOpen?: number
  /** Fusion : comptes jamais connectes de la source non repris (archives ou rattaches ailleurs). */
  dormantAccounts?: number
  /** Fusion : comptes Store de la source desactives (la cible a le sien). */
  storeDisabled?: number[]
  /** Fusion : comptes Store de la source a rattacher a la main (la cible n'en a pas). */
  storeToReattach?: Array<{ userIdStore: number; sourceCip: string | null; targetCip: string | null }>
  /** « Tout le filtre » : lignes du filtre, eligibles, ignorees (non eligibles). */
  total?: number
  eligible?: number
  ignored?: number
}

/**
 * GET gers_creation_preview?id= : ce que « Créer la fiche » écrirait sans saisie, pour pré-remplir
 * la boîte. `owner.source` = 'cnop' | 'gers' (null : pas de nom) ; `email` = e-mail Eurodep LIBRE
 * à la lecture (null sinon), `eurodepEmail` = celui du flux, libre ou non.
 */
export type GersCreationPreview = {
  cip: string
  owner: { source: 'cnop' | 'gers' | null; lastname: string; firstname: string; rpps: string | null }
  email: string | null
  eurodepEmail: string | null
  phone: string | null
}

/**
 * GET gers_creation_siret_check?id=&siret= : le SIRET saisi dans « Créer la fiche », avant la
 * validation. `insee.status` ne bloque pas ; `companies` non vide = SIRET d'une autre fiche, refusé.
 */
export type GersCreationSiretCheck = {
  siret: string
  valid: boolean
  insee: {
    status: 'active' | 'inactive' | 'not_found' | 'unavailable'
    companyName: string | null
    address: string | null
    postalCode: string | null
    city: string | null
    naf: string | null
  } | null
  samePostalCode: boolean | null
  companies: Array<{ id: number; cip: string | null; companyName: string | null }>
}

/**
 * Apercu de POST gers_reconciliations/apply_filter (dryRun: true) : rien n'est ecrit. Au-dela
 * de `cap` lignes eligibles, l'application est refusee (422) : affiner le filtre.
 */
export type GersApplyFilterPreview = {
  dryRun: true
  action: GersApplyAction
  total: number
  eligible: number
  ignored: number
  cap: number
  overCap: boolean
}

/**
 * Application en masse des propositions SURES de nom de titulaire (titulaires « sans nom »,
 * POST gers_reconciliations/apply_sure_owner_names). Meme liste UNIQUE que GERS_APPLY_ACTIONS :
 * consommee par le schema Zod de la mutation (src/ApiMutationEndpoint.ts) ET par le selecteur
 * de source du dialog.
 */
export const GERS_SURE_OWNER_NAME_SOURCES = ['all', 'gers', 'cnop'] as const

export type GersSureOwnerNameSource = (typeof GERS_SURE_OWNER_NAME_SOURCES)[number]

/**
 * Motifs d'abstention (compteur `reasons` de la reponse, cles i18n
 * `components.gers.sureOwnerNames.reason.*`). Cf. GERS_REFERENCE.md
 * « Application en masse des propositions SURES ».
 */
export const GERS_SURE_OWNER_NAME_REASONS = [
  'stale_proposal',
  'archived_cip',
  'shared_owner',
  'no_proposal',
  'cnop_gers_conflict',
  'low_score',
  'ambiguous_establishment',
  'no_holder',
  'multiple_holders',
  'holder_incomplete',
  'owner_conflict',
  'rpps_used_by_other_user',
  'cnop_differs',
  'up_to_date',
  'error',
] as const

export type GersSureOwnerNameReason = (typeof GERS_SURE_OWNER_NAME_REASONS)[number]

/** Compteur par motif : une cle absente ou a 0 vaut « aucune fiche ecartee pour ce motif ». */
export type GersSureOwnerNameReasonCounts = Partial<Record<GersSureOwnerNameReason, number>>

/**
 * Avis de rapprochement GERS/CNOP (compteur `opinions` de l'apercu, cles i18n
 * `components.gers.sureOwnerNames.opinion.*`). CNOP prime sur le GERS : `cnop_prioritaire`
 * dit que le CNOP remplace la proposition GERS.
 */
export const GERS_SURE_OWNER_NAME_OPINIONS = [
  'accord',
  'cnop_prioritaire',
  'cnop_hors_gers',
  'contradiction',
  'rien_de_fiable',
  'non_consulte',
] as const

export type GersSureOwnerNameOpinion = (typeof GERS_SURE_OWNER_NAME_OPINIONS)[number]

/** Compteur par avis : une cle absente ou a 0 vaut « aucune fiche sur cet avis ». */
export type GersSureOwnerNameOpinionCounts = Partial<Record<GersSureOwnerNameOpinion, number>>

export type GersSureOwnerNameDecisionCounts = {
  would_complete: number
  skipped: number
}

/** Repartition par source (GERS / CNOP / aucune proposition) d'un apercu ou d'une demande. */
export type GersSureOwnerNameBucket = {
  total: number
  decisions: GersSureOwnerNameDecisionCounts
  reasons: GersSureOwnerNameReasonCounts
}

/** Reponse 200 de dryRun:true — rien n'est ecrit, aucun appel externe. */
export type GersSureOwnerNamesDryRunResult = {
  dryRun: true
  source: GersSureOwnerNameSource
  minScore: number
  total: number
  decisions: GersSureOwnerNameDecisionCounts
  reasons: GersSureOwnerNameReasonCounts
  /** Avis de rapprochement GERS/CNOP, CNOP prioritaire. Un compteur vide vaut `{}`. */
  opinions: GersSureOwnerNameOpinionCounts
  bySource: {
    gers: GersSureOwnerNameBucket
    cnop: GersSureOwnerNameBucket
    none: GersSureOwnerNameBucket
  }
}

/** Reponse 202 de dryRun:false — traitement mis en file, rapport envoye par e-mail au demandeur. */
export type GersSureOwnerNamesQueuedResult = {
  dryRun: false
  status: 'queued'
  source: GersSureOwnerNameSource
  minScore: number
  message: string
}

/**
 * Pre-remplissage du « Changement de proprietaire » ouvert depuis une tache de cession
 * (GET pharmacies/:companyId/owner_change_proposal, lot 7 « Qualite des fiches ») : titulaires
 * proposes (CNOP d'abord, hors titulaire actuel ; a defaut GERS), SIRET connu d'Eurodep s'il
 * differe. L'e-mail du nouveau titulaire n'est propose par personne.
 */
export type PharmacyOwnerChangeProposal = {
  companyId: number
  currentSiret: string | null
  currentHolder: string | null
  holders: Array<{ lastname: string; firstname: string | null; rpps: string | null }>
  holderSource: 'cnop' | 'gers' | null
  /** Statut de la recherche CNOP : ok | no_holder | low_score | no_match | unavailable. */
  cnopStatus: string | null
  cnopEstablishment: { name: string | null; score: number } | null
  gersOwner: { name: string; updatedAt: string | null } | null
  siret: string | null
  /** pharmacist = SIRET déclaré par le pharmacien, prioritaire sur Eurodep. */
  siretSource: 'eurodep' | 'pharmacist' | null
}

/**
 * Fiche dont le titulaire porte un e-mail PROVISOIRE (`<CIP>@mail.fr`) — GET
 * /pharmacy_provisional_owner_emails. La proposition est un ancien compte de la fiche deja
 * connecte : candidat a « Retablir comme titulaire » (onglet Anciens proprietaires).
 */
export type ProvisionalOwnerEmailRow = {
  companyId: number
  cip: string | null
  companyName: string | null
  zipCode: string | null
  city: string | null
  validationStatus: string | null
  companySiret?: string | null
  ownerUserId: number
  ownerFirstname: string | null
  ownerLastname: string | null
  ownerEmail: string | null
  ownerCreatedAt: string | null
  /** Geste qui a pose ce titulaire, quand l'historique le connait (absent pour un import). */
  installedAt?: string | null
  installedBy?: string | null
  installedReason?: string | null
  /** Ligne d'historique proposee : c'est elle que retablit le geste « Retablir ». */
  proposalHistoryId?: number | null
  proposalUserId?: number | null
  proposalFirstname?: string | null
  proposalLastname?: string | null
  proposalEmail?: string | null
  proposalLastLogin?: string | null
  /** `owner_change` : titulaire remplace sur la fiche ; `merge_source` : titulaire d'une fiche absorbee. */
  proposalEventType?: 'owner_change' | 'merge_source' | null
  proposalEventAt?: string | null
  /** Meme SIREN que la fiche (true), autre SIREN (false), inconnu (absent). */
  proposalSameSiren?: boolean | null
  /** SIRET de la periode proposee : la fiche le reprend au retablissement s'il differe du sien. */
  proposalSiret?: string | null
  /** Le compte propose porte le prenom et le nom du titulaire provisoire. */
  proposalSamePerson: boolean
  otherProposals: number
}

/** GET /pharmacy_provisional_owner_email_counts */
export type ProvisionalOwnerEmailCounts = {
  total: number
  withProposal: number
  withSamePersonProposal: number
  withSameSirenProposal: number
}

/** POST /pharmacy_provisional_owner_emails/{companyId}/restore */
export type ProvisionalOwnerRestoreResult = {
  companyId: number
  ownerUserId: number
  ownerEmail: string | null
  replacedEmail: string | null
  siretFrom: string | null
  siretTo: string | null
  inseeStatus: string | null
}
